import type { ComputedRef, Ref } from 'vue';
import type { ResultItem } from '@/utils/file';
import type {
  BookedJobsView,
  InitialData as BookedJobsSearchFormInitialData,
  ValidData as BookedJobsSearchFormValidData,
} from '@/components/searchForms/BookedJobsSearchForm.vue';
import type { DataItem as BookedJobsTableDataItem } from '@/components/tables/BookedJobsTable.vue';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isCancel } from 'axios';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import { useUserStore } from '@/stores/useUserStore';
import {
  fetchGetJobsBookedByCustomer,
  fetchGetJobsBookedByUser,
} from '@/helpers/fetchers';
import * as routeQueryHelpers from '@/helpers/routeQuery';
import { sendError } from '@/helpers/sentry';
import * as bookedJobsTableHelpers from '@/helpers/tables/bookedJobs';
import * as routeNames from '@/constants/routeNames';

export interface BookedJobsViewComposable {
  view: ComputedRef<BookedJobsView>;
  initialData: ComputedRef<BookedJobsSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  curPage: Readonly<Ref<number>>;
  pageSize: Readonly<Ref<number>>;
  total: Ref<number>;
  tableTitle: ComputedRef<string>;
  results: Ref<BookedJobsTableDataItem[]>;
  resultsForCsv: ComputedRef<ResultItem[]>;
  search: (formData: BookedJobsSearchFormValidData) => void;
  setView: (view: BookedJobsView) => void;
}

export default function useBookedJobsView(): BookedJobsViewComposable {
  const route = useRoute();
  const router = useRouter();
  const userStore = useUserStore();
  const { getLast14DaysRange } = useCurrentDateString();

  const { queryDates, queryCurPage, queryPageSize } = useCommonRouteQuery({
    queryDates: getLast14DaysRange(),
  });

  const userTimeZone = computed(() => userStore.user.timezone);

  const view = computed<BookedJobsView>(() =>
    route.query.view === 'customer' ? 'customer' : 'user',
  );
  const user = computed(() =>
    routeQueryHelpers.getQueryStringValue(route.query.user),
  );
  const customer = computed(() =>
    routeQueryHelpers.getQueryStringValue(route.query.customer),
  );

  const initialData = computed<BookedJobsSearchFormInitialData>(() => ({
    dates: queryDates.value,
    user: user.value,
    customer: customer.value,
  }));

  const isFetchingData = ref(false);
  const results = ref<BookedJobsTableDataItem[]>([]);
  const total = ref(0);

  const tableTitle = computed(() =>
    view.value === 'customer'
      ? customer.value || 'My company'
      : user.value || 'My',
  );

  const resultsForCsv = computed<ResultItem[]>(() =>
    results.value.map(({ uniqueKey, fold, ...csvRow }) => csvRow),
  );

  let controller: AbortController | undefined;

  async function fetchJobs(): Promise<void> {
    if (!queryDates.value) return;
    if (controller) controller.abort();
    const thisController = new AbortController();
    controller = thisController;
    isFetchingData.value = true;
    try {
      const response =
        view.value === 'customer'
          ? await fetchGetJobsBookedByCustomer(
              userTimeZone.value,
              {
                customer: customer.value,
                dates: queryDates.value,
                page: queryCurPage.value,
                pageSize: queryPageSize.value,
              },
              { signal: thisController.signal },
            )
          : await fetchGetJobsBookedByUser(
              userTimeZone.value,
              {
                user: user.value,
                dates: queryDates.value,
                page: queryCurPage.value,
                pageSize: queryPageSize.value,
              },
              { signal: thisController.signal },
            );
      // Mapping errors aren't reported by the fetcher (it only covers the
      // request itself), so they get their own report here.
      try {
        total.value = response.count;
        results.value = response.results.map((job) =>
          bookedJobsTableHelpers.getTableItem(job, userTimeZone.value),
        );
      } catch (err) {
        total.value = 0;
        results.value = [];
        sendError(err, 'useBookedJobsView.fetchJobs mapping');
      }
    } catch (err) {
      if (!isCancel(err)) {
        total.value = 0;
        results.value = [];
      }
    } finally {
      if (controller === thisController) isFetchingData.value = false;
    }
  }

  function pushQuery(query: Record<string, unknown>): void {
    const newQuery = { ...route.query, ...query, page: '1' };
    const isReplace = _isEqual(route.query, newQuery);
    void router.push({
      name: routeNames.BOOKED_JOBS,
      query: newQuery,
      replace: isReplace,
    });
  }

  function search(formData: BookedJobsSearchFormValidData): void {
    if (!formData.dates) return;
    pushQuery({
      dates: [
        format(formData.dates[0], 'yyyy-MM-dd'),
        format(formData.dates[1], 'yyyy-MM-dd'),
      ].join(','),
      user: formData.user || undefined,
      customer: formData.customer || undefined,
    });
  }

  function setView(newView: BookedJobsView): void {
    pushQuery({ view: newView, user: undefined, customer: undefined });
  }

  watch(
    [queryDates, view, user, customer, queryCurPage, queryPageSize],
    () => {
      void fetchJobs();
    },
    { immediate: true },
  );

  onUnmounted(function () {
    if (controller) controller.abort();
  });

  return {
    view,
    initialData,
    isFetchingData,
    curPage: queryCurPage,
    pageSize: queryPageSize,
    total,
    tableTitle,
    results,
    resultsForCsv,
    search,
    setView,
  };
}
